import { useEffect, useState, type RefObject } from 'react';

/** Ignore jitters smaller than this (px) - a resting finger still moves. */
const JITTER = 4;
/** Never hide near the top: the control is wanted before reading starts. */
const TOP_ZONE = 40;

/**
 * True while the user is scrolling DOWN anywhere inside `ref`, false once
 * they scroll back up - the reading-app rule for a floating button that
 * would otherwise sit on top of the text being read.
 *
 * One capture listener on the container catches every nested scroller (each
 * lesson tab scrolls its own box), so no tab has to report its scrolling.
 * `resetKey` shows the control again when it changes (a new tab).
 */
export function useHideOnScrollDown(
  ref: RefObject<HTMLElement>,
  resetKey?: unknown
): boolean {
  const [hidden, setHidden] = useState(false);

  useEffect(() => setHidden(false), [resetKey]);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const last = new WeakMap<EventTarget, number>();
    const onScroll = (e: Event) => {
      const el = e.target;
      if (!(el instanceof HTMLElement)) return;
      const prev = last.get(el) ?? el.scrollTop;
      const delta = el.scrollTop - prev;
      last.set(el, el.scrollTop);
      if (Math.abs(delta) < JITTER) return;
      setHidden(delta > 0 && el.scrollTop > TOP_ZONE);
    };
    root.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () =>
      root.removeEventListener('scroll', onScroll, { capture: true });
  }, [ref]);

  return hidden;
}
